// ImageUploadCell.js - per-unit reference image.
// While editing, new picks keep a dataUrl preview. Before save/submit, uploadAllQuotationImages
// replaces dataUrl with { name, file_id }. Saved quotes resolve file_id via Interior files preview_url.
import { ImagePlus, Loader2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { resolveQuotationFilePreviewUrl } from './interiorQuotationApi';

const ImageUploadCell = ({ image, onChange, siteId = null }) => {
  const inputRef = useRef(null);
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (image?.dataUrl) {
      setResolvedUrl(image.dataUrl);
      setIsResolving(false);
      return undefined;
    }

    const fileId = image?.file_id;
    if (!fileId || !siteId) {
      setResolvedUrl(null);
      setIsResolving(false);
      return undefined;
    }

    setIsResolving(true);
    resolveQuotationFilePreviewUrl(siteId, fileId)
      .then((url) => {
        if (!cancelled) setResolvedUrl(url || null);
      })
      .catch(() => {
        if (!cancelled) setResolvedUrl(null);
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [image?.dataUrl, image?.file_id, siteId]);

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onChange({ name: file.name, dataUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const previewSrc = image?.dataUrl || resolvedUrl || null;

  if (previewSrc || image?.file_id || image?.name) {
    return (
      <div className="relative mt-1.5 inline-flex">
        <div className="block h-10 w-10 overflow-hidden rounded-md border border-gray-200">
          {isResolving && !previewSrc ? (
            <span className="flex h-full w-full items-center justify-center bg-gray-50">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
            </span>
          ) : previewSrc ? (
            <img src={previewSrc} alt={image?.name || 'Unit reference'} className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full items-center justify-center bg-gray-50 text-[9px] text-gray-500">
              file
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowRemoveConfirm(true)}
          title="Remove image"
          className="absolute -right-1.5 -top-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600"
        >
          <X className="h-2.5 w-2.5" />
        </button>

        <ConfirmDialog
          isOpen={showRemoveConfirm}
          title="Remove image?"
          message={`This will remove "${image?.name || 'this image'}" from the row. This cannot be undone.`}
          confirmLabel="Remove"
          onCancel={() => setShowRemoveConfirm(false)}
          onConfirm={() => {
            setShowRemoveConfirm(false);
            onChange(null);
          }}
        />
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        title="Upload reference image"
        className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-500 hover:border-teal-400 hover:text-teal-700"
      >
        <ImagePlus className="h-3 w-3" /> Image
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFileSelected} className="hidden" />
    </>
  );
};

export default ImageUploadCell;
